import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Accordion from "react-bootstrap/Accordion";
import { useFormik } from "formik";
import * as Yup from "yup";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import MultiSelect from '../common/MultiSelect';
import { useDispatch, useSelector } from 'react-redux';
import { saveResponse } from "../../store/slices/dataFilter";
import { getAssociationType } from "../../store/slices/getAssociationType";
import { getFilterData } from "../../store/slices/getFilterData";
import { getAttributes } from "../../store/slices/getPImAttributes";
import {getCategories} from "../../store/slices/getCategories";
import {getAssetsFamilies} from "../../store/slices/assetsFamiliesSlice";
import LocalePopup from './localePopup';
import CurrencyPopup from './CurrencyPopup';

const validationSchema = Yup.object({
  locale: Yup.string().required("Locale is Required"),
  currency: Yup.string().required("Currency is Required"),
  scope: Yup.string().required("Channel is Required"),
  completeness: Yup.number()
    .max(100, "Completeness should be less than or equal to 100%")
    .nullable(),
  enabled: Yup.string(),
  lastxmenu: Yup.string(),
  lastxdays: Yup.number().when("lastxmenu", {
    is: (val) => !!val,
    then: (schema) => schema.required("This field is required when frequency type is selected"),
    otherwise: (schema) => schema.notRequired()
  }),
  advanceJson: Yup.string(),
  categoryTree: Yup.string(),
});

function DataFilter({ showToast }) {
  const data = useSelector(state => state.filtersOptions.data);
  const savedData = useSelector((state) => state.config.data);
  const configId = useSelector((state) => state.saveCreds.configId);
  const categoriesTreeOptions = useSelector((state)=>state.rootCategories.optionsData);
  const categoriesTreeObj = useSelector((state)=> state.rootCategories.data);

  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // const [localToggle, setLocalToggle] = useState(false);
  // const [currencyToggle, setCurrencyToggle] = useState(false);

  const formik = useFormik({
    initialValues: {
      locale: "",
      currency: "",
      completeness: "",
      scope: "",
      enabled: "",
      lastxmenu: "",
      lastxdays: "",
      advanceJson: "",
      productskus: "",
      productmodels: "",
      categoryTrees: [],
      multiLocale: []
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: (values) => {
      setIsSubmitting(true);

      let parsedJson = null;

      try {
        if (values.advanceJson?.trim()) {
          parsedJson = JSON.parse(values.advanceJson);
        }
      } catch (error) {
        alert("Invalid JSON! Please enter a valid JSON");
        setIsSubmitting(false);
        return;
      }

      const prodSKU = typeof values.productskus.trim() === "string"
        ? values.productskus.trim().split(",").map(value => value.trim()).filter(Boolean)
        : [];

      const modelID = typeof values.productmodels.trim() === "string"
        ? values.productmodels.trim().split(",").map(value => value.trim()).filter(Boolean)
        : [];

      const categoryTreeArray = values.categoryTrees.reduce((acc, item) => {
        acc[item.value] = item.value;
        return acc;
      }, {});

      const multiLocaleArray = values.multiLocale.map((item)=>item.value);

      const req = {
        basicFilter: {
          dataFilter: {
            locale: values.locale,
            multiLocale : multiLocaleArray || {},
            currency: values.currency,
            scope: values.scope,
            completeness: values.completeness?.toString(),
            enabled: values.enabled,
            ...(values.lastxmenu === 'minutes' && { lastXMinutes: values.lastxdays?.toString() }),
            ...(values.lastxmenu === 'hours' && { lastXHours: values.lastxdays?.toString() }),
            ...(values.lastxmenu === 'days' && { lastXDays: values.lastxdays?.toString() }),
          },
          products: prodSKU,
          productModels: modelID,
          // categoryTree: categoryTreeArray,
          advanceJson: parsedJson || {},
          channelMapping : categoryTreeArray || {}
        },
        struConfigId: configId || savedData?.struConfigId
      };

      // First save the response
      dispatch(saveResponse(req))
        .then(() => {
          const targetConfigId = configId || savedData?.struConfigId;
          if (targetConfigId && values.locale) {
            // Dispatch all async actions and wait for them to complete
            return Promise.all([
              dispatch(getAttributes({ configId: targetConfigId, locale: values.locale })),
              dispatch(getAssociationType({ configId: targetConfigId, locale: values.locale })),
              dispatch(getFilterData({ configId: targetConfigId, locale: values.locale })),
              dispatch(getAssetsFamilies({configid:targetConfigId,locale: values.locale}))
            ]);
          }
        })
        .then(() => {
          // Show toast only after ALL operations are complete
          showToast("saveFilter");
        })
        .catch((error) => {
          console.error("Failed to save configuration:", error);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    },
  });

  const { values, errors, touched, handleChange, handleBlur, handleSubmit } = formik;

  const isSkuOrModelFilled = useMemo(() =>
    String(values.productskus || "").trim() !== '' ||
    String(values.productmodels || "").trim() !== '',
    [values.productskus, values.productmodels]
  );

  const isAdvanceJsonFilled = useMemo(() =>
    String(values.advanceJson || "").trim() !== '',
    [values.advanceJson]
  );

  const isEmptyObject = useCallback((obj) => {
    return obj && typeof obj === "object" &&
      Object.keys(obj).length === 0 &&
      obj.constructor === Object;
  }, []);

  useEffect(() => {
    if (savedData?.basicFilter) {
      const filterData = savedData.basicFilter?.dataFilter || {};
      const prodSKU = savedData.basicFilter?.products || [];
      const prodID = savedData.basicFilter?.productModels || [];
      const categoryTreeObj = savedData.basicFilter?.channelMapping || {};
      const multiLocalObj = savedData.basicFilter?.dataFilter?.multiLocale || {}

      let lastxmenu = '';
      let lastxdays = '';

      if (filterData.lastXDays || filterData.lastxdays) {
        lastxmenu = 'days';
        lastxdays = filterData.lastXDays || filterData.lastxdays;
      } else if (filterData.lastXHours || filterData.lastXhours) {
        lastxmenu = 'hours';
        lastxdays = filterData.lastXHours || filterData.lastXhours;
      } else if (filterData.lastXMinutes || filterData.lastXminutes) {
        lastxmenu = 'minutes';
        lastxdays = filterData.lastXMinutes || filterData.lastXminutes;
      }

      const categoryTrees = typeof categoryTreeObj === "object" ? Object.entries(categoryTreeObj).map(
        ([key, value]) => ({
          value: key,
          label: categoriesTreeObj.value || value
        })
      ) : [];

      const multiLocaleTrees = Array.isArray(multiLocalObj)
        ? multiLocalObj.map(locale => ({
            value: locale,
            label: locale,
          }))
        : [];

      formik.setValues({
        locale: filterData.locale || "",
        currency: filterData.currency || "",
        scope: filterData.scope || "",
        completeness: filterData.completeness || "",
        enabled: filterData.enabled || "",
        lastxmenu,
        lastxdays,
        advanceJson: !isEmptyObject(savedData.basicFilter.advanceJson)
          ? JSON.stringify(savedData.basicFilter.advanceJson)
          : "",
        productskus: Array.isArray(prodSKU) ? prodSKU.join(',') : prodSKU,
        productmodels: Array.isArray(prodID) ? prodID.join(',') : prodID,
        categoryTrees: categoryTrees || [],
        multiLocale: multiLocaleTrees || []
      });
    }
  }, [savedData, isEmptyObject, formik.resetForm]);

  const renderSelectOptions = useCallback((options) => {
    if (!options) return null;
    return options.map((option) => (
      <option key={option} value={option}>{option}</option>
    ));
  }, []);

  const renderMultiLocale = useCallback((options) => {
    if (!options) return null;
    return Object.entries(options)?.filter(([index, code])=> code != formik.values.locale)?.map(([index, code]) => ({
          value:code,
          label: code
        }));
  }, [formik.values.locale]);

  useEffect(()=>{
    const configID = configId || savedData?.struConfigId;
    const locale = formik.values.locale;
    if(!locale && !configID) return;
    dispatch(getCategories({configId:configID,locale}));
  }, [formik.values.locale])

  const renderChannels = useCallback((options) => {
    if (!options) return null;
    return options.map((option) => (
      <option key={option.code} value={option.code}>{option.displayName}</option>
    ));
  }, []);

  return (
    <Form onSubmit={handleSubmit}>
      <Accordion.Item eventKey="1" className="my-1 mt-4">
        <Accordion.Header>Data Filters</Accordion.Header>
        <Accordion.Body className="p-4">
          <div className="bg-light p-4 rounded mb-3">
            <h6 className="fw-bold mb-4">Product Data Filters</h6>
            <Row>
              <Col sm={4}>
                <Form.Group className="mb-3" controlId="locale">
                  <Form.Label>Default Locale</Form.Label>
                  <Form.Select
                    aria-label="Select locale"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.locale}
                    isInvalid={touched.locale && errors.locale}
                  >
                    <option value="">Select a locale</option>
                    {data?.pimLocales && renderSelectOptions(data.pimLocales)}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.locale}
                  </Form.Control.Feedback>
                  {/* <button type='button' className='modal_button' onClick={setLocalToggle}>Secondary Locale</button> */}
                </Form.Group>
              </Col>
              <Col sm={4}>
                <Form.Group className="mb-3" controlId="currency">
                  <Form.Label>Currency</Form.Label>
                  <Form.Select
                    aria-label="Select currency"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.currency}
                    isInvalid={touched.currency && errors.currency}
                  >
                    <option value="">Select a currency</option>
                    {data?.pimCurrencies && renderSelectOptions(data.pimCurrencies)}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.currency}
                  </Form.Control.Feedback>
                  {/* <button type='button' className='modal_button' onClick={setCurrencyToggle}>Edit markets pricing</button> */}
                </Form.Group>
              </Col>
              <Col sm={4}>
                <Form.Group className="mb-3" controlId="scope">
                  <Form.Label>Channel</Form.Label>
                  <Form.Select
                    aria-label="Select channel"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.scope}
                    isInvalid={touched.scope && errors.scope}
                  >
                    <option value="">Select a channel</option>
                    {data?.pimChannels && renderChannels(data.pimChannels)}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.scope}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-2">
              <Col sm={6}>
                <Form.Group className="mb-3" controlId="enabled">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    aria-label="Select status"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.enabled}
                    isInvalid={touched.enabled && errors.enabled}
                  >
                    <option value="">Select</option>
                    <option value="-1">All</option>
                    <option value="1">Enabled</option>
                    <option value="0">Disabled</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.enabled}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col sm={6}>
                <Form.Group className="mb-3" controlId="completeness">
                  <Form.Label>Completeness %</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="100"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.completeness}
                    isInvalid={touched.completeness && errors.completeness}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.completeness}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col sm={12} className="mb-3">
                <Form.Label>Secondary Locales</Form.Label>
                <MultiSelect  
                    name={"multiLocale"}
                    value={values.multiLocale}
                    onChange={formik.setFieldValue}
                    onBlur={formik.setFieldTouched}
                    touched={touched.categoryTrees}
                    options={data?.pimLocales && renderMultiLocale(data.pimLocales)}
                    isInvalid={touched.multiLocale && errors.multiLocale}
                    error={errors.multiLocale}
                    placeholder= "Select the multi locales"
                  />
              </Col>
            </Row>
            <Row>
              <Col sm={12} className="mb-3">
                <Form.Label>Category Trees</Form.Label>
                <MultiSelect  
                    name={"categoryTrees"}
                    value={values.categoryTrees}
                    onChange={formik.setFieldValue}
                    onBlur={formik.setFieldTouched}
                    touched={touched.categoryTrees}
                    options={categoriesTreeOptions}
                    isInvalid={touched.categoryTrees && errors.categoryTrees}
                    error={errors.categoryTrees}
                    placeholder= "Select the Category Trees"
                  />
              </Col>
            </Row>
            {/* {localToggle && <LocalePopup toggle={localToggle} setToggle={setLocalToggle} />}
            {currencyToggle && <CurrencyPopup toggle={currencyToggle} setToggle={setCurrencyToggle} />} */}
            <Form.Group className="mb-3" controlId="productskus">
              <Form.Label>Product SKUs - Locale, Currency, Channel filters will be applied</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder='Comma separated list of SKUs'
                spellCheck={false}
                className="px-3"
                value={values.productskus}
                isInvalid={touched.productskus && errors.productskus}
                disabled={isAdvanceJsonFilled}
              />
              <Form.Control.Feedback type="invalid">
                {errors.productskus}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="productmodels">
              <Form.Label>Product Model IDs - Locale, Currency, Channel filters will be applied</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder='Comma separated list of IDs'
                spellCheck={false}
                className="px-3"
                value={values.productmodels}
                isInvalid={touched.productmodels && errors.productmodels}
                disabled={isAdvanceJsonFilled}
              />
              <Form.Control.Feedback type="invalid">
                {errors.productmodels}
              </Form.Control.Feedback>
            </Form.Group>
          </div>

          <div className="bg-light p-4 rounded mb-3">
            <h6 className="fw-bold mb-4">Products Updated Since Last &apos;X&apos; Days/Hours/Minutes</h6>
            <Row className="mb-2">
              <Col sm={6}>
                <Form.Group className="mb-3" controlId="lastxmenu">
                  <Form.Label>Frequency Type</Form.Label>
                  <Form.Select
                    aria-label="Select frequency type"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.lastxmenu}
                    isInvalid={touched.lastxmenu && errors.lastxmenu}
                  >
                    <option value="">Select the frequency type</option>
                    <option value="minutes">Minutes</option>
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.lastxmenu}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col sm={6}>
                <Form.Group className="mb-3" controlId="lastxdays">
                  <Form.Label>
                    Last &apos;X&apos; {values.lastxmenu || 'Days/Hours/Minutes'}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={!values.lastxmenu}
                    value={values.lastxdays}
                    isInvalid={touched.lastxdays && errors.lastxdays}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.lastxdays}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </div>

          <div className="bg-light p-4 rounded">
            <h6 className="fw-bold mb-4">Advance Filters (JSON)</h6>
            <Form.Group className="mb-3" controlId="advanceJson">
              <Form.Control
                as="textarea"
                rows={3}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder='{"updated":[{"operator":"SINCE LAST N DAYS","value":"200"}],"enabled":[{"operator":"=","value":true}],"completeness":[{"operator":"=","value":100,"scope":"ecommerce"}]}'
                spellCheck={false}
                className="px-3 font-monospace"
                value={values.advanceJson}
                isInvalid={touched.advanceJson && errors.advanceJson}
                disabled={isSkuOrModelFilled}
              />
              <Form.Control.Feedback type="invalid">
                {errors.advanceJson}
              </Form.Control.Feedback>
            </Form.Group>
          </div>

          <Row className="mt-5 mx-3">
            <Col sm={12}>
              <Button
                variant="primary"
                type="submit"
                className="stru-btn"
                // disabled={isSubmitting}
              >
                Save Config
              </Button>
            </Col>
          </Row>
        </Accordion.Body>
      </Accordion.Item>
    </Form>
  );
}

export default DataFilter;